// API utilities and configuration

import axios, { AxiosInstance, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import type { ApiResponse } from '../types';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.civicvoiceofindia.org/api';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for authentication
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token from localStorage if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add CSRF token if available
    const csrfToken = localStorage.getItem('csrf_token');
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    // Handle successful responses
    if (response.data && !response.data.success) {
      // API returned success: false
      throw new Error(response.data.error || 'Request failed');
    }
    
    return response;
  },
  (error) => {
    // Handle error responses
    const message = error.response?.data?.error || error.message || 'Network error occurred';
    
    // Handle specific error codes
    if (error.response?.status === 401) {
      // Unauthorized - clear auth data and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('csrf_token');
      
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        toast.error('Session expired. Please log in again.');
        window.location.href = '/login';
      }
    } else if (error.response?.status === 403) {
      toast.error('You do not have permission to perform this action.');
    } else if (error.response?.status === 429) {
      toast.error('Too many requests. Please wait before trying again.');
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    } else if (!error.response) {
      toast.error('Network error. Please check your connection.');
    }
    
    return Promise.reject(error);
  }
);

// API helper functions
export const apiClient = {
  // Generic request methods
  async get<T>(url: string, params?: any): Promise<T> {
    const response = await api.get<ApiResponse<T>>(url, { params });
    return response.data.data as T;
  },

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await api.post<ApiResponse<T>>(url, data);
    return response.data.data as T;
  },

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await api.put<ApiResponse<T>>(url, data);
    return response.data.data as T;
  },

  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await api.patch<ApiResponse<T>>(url, data);
    return response.data.data as T;
  },

  async delete<T>(url: string): Promise<T> {
    const response = await api.delete<ApiResponse<T>>(url);
    return response.data.data as T;
  },

  // File upload with progress
  async uploadFile(
    url: string, 
    file: File, 
    onProgress?: (progress: number) => void
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data.data;
  },
};

// Auth API endpoints
export const authApi = {
  async login(email: string, password: string) {
    return apiClient.post('/api/auth/login', { email, password });
  },

  async register(data: {
    email: string;
    password: string;
    full_name: string;
    phone?: string;
    location?: string;
  }) {
    return apiClient.post('/api/auth/register', data);
  },

  async logout() {
    return apiClient.post('/api/auth/logout');
  },

  async getMe() {
    return apiClient.get('/api/auth/me');
  },

  async updateProfile(data: any) {
    return apiClient.put('/api/auth/profile', data);
  },
};

// Chapters API endpoints
export const chaptersApi = {
  async getChapters(params?: {
    page?: number;
    limit?: number;
    location?: string;
    status?: string;
  }) {
    return apiClient.get('/api/chapters', params);
  },

  async getChapter(slug: string) {
    return apiClient.get(`/api/chapters/${slug}`);
  },

  async createChapter(data: any) {
    return apiClient.post('/api/chapters', data);
  },

  async updateChapter(slug: string, data: any) {
    return apiClient.put(`/api/chapters/${slug}`, data);
  },

  async joinChapter(slug: string) {
    return apiClient.post(`/api/chapters/${slug}/join`);
  },

  async leaveChapter(slug: string) {
    return apiClient.post(`/api/chapters/${slug}/leave`);
  },
};

// Events API endpoints
export const eventsApi = {
  async getEvents(params?: {
    page?: number;
    limit?: number;
    chapter_id?: number;
    status?: string;
    upcoming_only?: boolean;
  }) {
    return apiClient.get('/api/events', params);
  },

  async getEvent(id: number) {
    return apiClient.get(`/api/events/${id}`);
  },

  async createEvent(data: any) {
    return apiClient.post('/api/events', data);
  },

  async updateEvent(id: number, data: any) {
    return apiClient.put(`/api/events/${id}`, data);
  },

  async registerForEvent(id: number) {
    return apiClient.post(`/api/events/${id}/register`);
  },

  async unregisterFromEvent(id: number) {
    return apiClient.post(`/api/events/${id}/unregister`);
  },
};

// Reports API endpoints
export const reportsApi = {
  async getReports(params?: {
    page?: number;
    limit?: number;
    chapter_id?: number;
    author_id?: number;
    report_type?: string;
  }) {
    return apiClient.get('/api/reports', params);
  },

  async getReport(id: number) {
    return apiClient.get(`/api/reports/${id}`);
  },

  async createReport(data: any) {
    return apiClient.post('/api/reports', data);
  },

  async updateReport(id: number, data: any) {
    return apiClient.put(`/api/reports/${id}`, data);
  },

  async deleteReport(id: number) {
    return apiClient.delete(`/api/reports/${id}`);
  },
};

// Dashboard API endpoints
export const dashboardApi = {
  async getStats() {
    return apiClient.get('/api/stats/dashboard');
  },
};

// File upload API endpoints
export const filesApi = {
  async getPresignedUrl(data: {
    filename: string;
    content_type: string;
    entity_type: string;
    entity_id: number;
    is_public?: boolean;
  }) {
    return apiClient.post('/api/upload/presign', data);
  },

  async uploadFile(file: File, onProgress?: (progress: number) => void) {
    return apiClient.uploadFile('/api/upload', file, onProgress);
  },

  async getFile(id: number) {
    return apiClient.get(`/api/files/${id}`);
  },
};

// Utility functions
export const formatApiError = (error: any): string => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export const isNetworkError = (error: any): boolean => {
  return !error.response && error.code === 'NETWORK_ERROR';
};

export const isTimeoutError = (error: any): boolean => {
  return error.code === 'ECONNABORTED';
};

export const retryRequest = async <T>(
  requestFn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      
      // Don't retry on client errors (4xx)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        throw error;
      }
      
      // Don't retry on the last attempt
      if (i === maxRetries) {
        break;
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
    }
  }
  
  throw lastError;
};

export default api;
